import dayjs from "dayjs";
import { TFunction } from "i18next";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IFitbitSleepSession } from "../../../../../../@types/Fitbit";
import {
  formatHoursAndMinutes,
  LONG_DATE_TIME_FORMAT,
} from "../../../../../../utils/dateFormatter";
import { FitbitSleepStagesBarChart } from "../FitbitSleepStagesChart/FitbitSleepStagesBarChart";
import { FitbitSleepStagesChart } from "../FitbitSleepStagesChart/FitbitSleepStagesChart";
import {
  Body,
  Container,
  EmptyState,
  Header,
  StatLabel,
  StatRow,
  StatsContainer,
  StatsTitle,
  StatSwatch,
  StatValue,
  Subtitle,
  Title,
} from "./FitbitSleepSelectedSession.styles";

type StageKey = "awake" | "rem" | "light" | "deep";

// EN-5191: a factory, not a module constant. A constant is evaluated at import time, before
// i18next is initialised, so every label would come back undefined.
const buildStageDisplay = (t: TFunction): Record<StageKey, string> => ({
  awake: t("patientPage_measurementModal_sleep_awake"),
  rem: t("patientPage_measurementModal_sleep_rem"),
  light: t("patientPage_measurementModal_sleep_light"),
  deep: t("patientPage_measurementModal_sleep_deep"),
});

const STAGE_COLOR: Record<StageKey, string> = {
  awake: "#EB5A7B",
  rem: "#A8C6FF",
  light: "#6697F4",
  deep: "#58C488",
};

const STAGE_ORDER: StageKey[] = ["awake", "rem", "light", "deep"];

type FitbitSleepSelectedSessionProps = {
  session?: IFitbitSleepSession;
};

function formatMinutesAsHM(minutes: number | null | undefined): string {
  if (minutes == null) return "-";
  return formatHoursAndMinutes(minutes / 60);
}

export function FitbitSleepSelectedSession({
  session,
}: FitbitSleepSelectedSessionProps) {
  const { t } = useTranslation();
  const stageDisplay = useMemo(() => buildStageDisplay(t), [t]);

  if (!session) {
    return (
      <Container>
        <Header>
          <Title>
            {t("patientPage_measurementModal_sleep_selectedSession")}
          </Title>
          <Subtitle>
            {t("patientPage_measurementModal_sleep_clickBar")}
          </Subtitle>
        </Header>
        <EmptyState>
          {t("patientPage_measurementModal_sleep_noSession")}
        </EmptyState>
      </Container>
    );
  }

  // Wall-clock strings: parse without TZ conversion. dayjs treats ISO-without-Z as local.
  const start = dayjs(session.startTime);
  const end = dayjs(session.endTime);
  const dateLabel = `${start.format("HH:mm")} - ${end.format(
    LONG_DATE_TIME_FORMAT
  )}`;

  const { stageSummary } = session;
  const hasSegments = session.segments.length > 0;

  const stageMinutes: Record<StageKey, number | null | undefined> = {
    awake: stageSummary?.awakeMinutes,
    rem: stageSummary?.remMinutes,
    light: stageSummary?.lightMinutes,
    deep: stageSummary?.deepMinutes,
  };

  return (
    <Container>
      <Header>
        <Title>
          {`${formatMinutesAsHM(session.minutesAsleep)} - ${t(
            "patientPage_measurementModal_sleep_selectedSession"
          )}`}
        </Title>
        <Subtitle>{dateLabel}</Subtitle>
      </Header>

      <Body>
        {hasSegments ? (
          <FitbitSleepStagesChart session={session} height={320} />
        ) : (
          <FitbitSleepStagesBarChart stageSummary={stageSummary} height={320} />
        )}

        <StatsContainer>
          <StatsTitle>
            {t("patientPage_measurementModal_sleep_sleepStages")}
          </StatsTitle>
          {STAGE_ORDER.map((stage) => (
            <StatRow key={stage}>
              <StatLabel>
                <StatSwatch style={{ background: STAGE_COLOR[stage] }} />
                {stageDisplay[stage]}
              </StatLabel>
              <StatValue>{formatMinutesAsHM(stageMinutes[stage])}</StatValue>
            </StatRow>
          ))}
          <StatRow>
            <StatLabel style={{ fontWeight: 600 }}>
              {t("patientPage_measurementModal_sleep_total")}
            </StatLabel>
            <StatValue style={{ fontWeight: 600 }}>
              {formatMinutesAsHM(session.minutesAsleep)}
            </StatValue>
          </StatRow>
        </StatsContainer>
      </Body>
    </Container>
  );
}
